/**
 * Data-driven CSS property definitions for StyleClassEditor
 * Each property group defines how CSS properties should be edited and rendered
 */

// CSS modifiers (pseudo-classes and pseudo-elements) with search tags
// Moved here to avoid circular reference
export const modifierDefinitions = {
  '': { name: 'None', description: 'No modifier', tags: [] },
  'hover': { name: 'Hover', description: 'When element is hovered', tags: ['hover', 'mouse', 'interaction', 'state'] },
  'active': { name: 'Active', description: 'When element is being clicked', tags: ['active', 'click', 'press', 'state'] },
  'focus': { name: 'Focus', description: 'When element has keyboard focus', tags: ['focus', 'keyboard', 'accessibility', 'state'] },
  'visited': { name: 'Visited', description: 'For visited links', tags: ['visited', 'link', 'history', 'state'] },
  'link': { name: 'Link', description: 'For unvisited links', tags: ['link', 'anchor', 'href', 'state'] },
  'checked': { name: 'Checked', description: 'For checked form elements', tags: ['checked', 'form', 'checkbox', 'radio', 'state'] },
  'disabled': { name: 'Disabled', description: 'For disabled form elements', tags: ['disabled', 'form', 'inactive', 'state'] },
  'enabled': { name: 'Enabled', description: 'For enabled form elements', tags: ['enabled', 'form', 'active', 'state'] },
  'valid': { name: 'Valid', description: 'For valid form inputs', tags: ['valid', 'form', 'validation', 'state'] },
  'invalid': { name: 'Invalid', description: 'For invalid form inputs', tags: ['invalid', 'form', 'validation', 'error', 'state'] },
  'required': { name: 'Required', description: 'For required form fields', tags: ['required', 'form', 'validation', 'state'] },
  'optional': { name: 'Optional', description: 'For optional form fields', tags: ['optional', 'form', 'validation', 'state'] },
  'first-child': { name: 'First Child', description: 'First child element', tags: ['first', 'child', 'position', 'structure'] },
  'last-child': { name: 'Last Child', description: 'Last child element', tags: ['last', 'child', 'position', 'structure'] },
  'nth-child': { name: 'Nth Child', description: 'Nth child element (specify n)', needsValue: true, tags: ['nth', 'child', 'position', 'structure', 'number'] },
  'first-of-type': { name: 'First of Type', description: 'First element of its type', tags: ['first', 'type', 'position', 'structure'] },
  'last-of-type': { name: 'Last of Type', description: 'Last element of its type', tags: ['last', 'type', 'position', 'structure'] },
  'nth-of-type': { name: 'Nth of Type', description: 'Nth element of its type (specify n)', needsValue: true, tags: ['nth', 'type', 'position', 'structure', 'number'] },
  'only-child': { name: 'Only Child', description: 'Only child element', tags: ['only', 'child', 'position', 'structure'] },
  'empty': { name: 'Empty', description: 'Element with no children', tags: ['empty', 'content', 'structure'] },
  'not': { name: 'Not', description: 'Elements that don\'t match selector (specify selector)', needsValue: true, tags: ['not', 'exclude', 'selector', 'advanced'] }
}

export const propertyDefinitions = {
  background: {
    label: 'Background',
    icon: 'photo',
    description: 'Background color, image, and positioning',
    tags: ['background', 'color', 'image', 'bg', 'backdrop', 'fill', 'gradient'],
    // No specific editor - will use GenericPropertyGroupEditor
    properties: {
      'background': {
        type: 'text',
        label: 'Background (all)',
        placeholder: 'linear-gradient(90deg, #5DDEF4 0%, #00AAC1 100%), url(...)',
        default: ''
      },
      'background-color': {
        type: 'color',
        label: 'Color',
        placeholder: 'transparent, #ffffff, rgb(255,255,255)',
        default: ''
      },
      'background-image': {
        type: 'image',
        label: 'Image',
        placeholder: 'url(...) or none',
        default: ''
      },
      'background-image-cdn': {
        type: 'text',
        label: 'CDN Path',
        placeholder: 'Auto-generated CDN path',
        readOnly: true,
        default: ''
      },
      'background-size': {
        type: 'select',
        label: 'Size',
        options: [
          { value: '', name: 'Default' },
          { value: 'cover', name: 'Cover' },
          { value: 'contain', name: 'Contain' },
          { value: 'auto', name: 'Auto' },
          { value: '100%', name: '100%' },
          { value: '50%', name: '50%' }
        ],
        default: ''
      },
      'background-position': {
        type: 'select',
        label: 'Position',
        options: [
          { value: '', name: 'Default' },
          { value: 'center', name: 'Center' },
          { value: 'top', name: 'Top' },
          { value: 'bottom', name: 'Bottom' },
          { value: 'left', name: 'Left' },
          { value: 'right', name: 'Right' },
          { value: 'top left', name: 'Top Left' },
          { value: 'top right', name: 'Top Right' },
          { value: 'bottom left', name: 'Bottom Left' },
          { value: 'bottom right', name: 'Bottom Right' }
        ],
        default: ''
      },
      'background-repeat': {
        type: 'select',
        label: 'Repeat',
        options: [
          { value: '', name: 'Default' },
          { value: 'no-repeat', name: 'No Repeat' },
          { value: 'repeat', name: 'Repeat' },
          { value: 'repeat-x', name: 'Repeat X' },
          { value: 'repeat-y', name: 'Repeat Y' }
        ],
        default: ''
      },
      'background-attachment': {
        type: 'select',
        label: 'Attachment',
        options: [
          { value: '', name: 'Default' },
          { value: 'scroll', name: 'Scroll' },
          { value: 'fixed', name: 'Fixed' },
          { value: 'local', name: 'Local' }
        ],
        default: ''
      }
    }
  },

  spacing: {
    label: 'Spacing',
    icon: 'arrows-expand',
    description: 'Margin and padding spacing',
    tags: ['spacing', 'margin', 'padding', 'gap', 'space', 'whitespace'],
    // No specific editor - will use GenericPropertyGroupEditor
    properties: {
      'margin': {
        type: 'text',
        label: 'Margin (all)',
        placeholder: '10px, 1rem, 10px 20px, etc.',
        default: ''
      },
      'margin-top': { type: 'text', label: 'Margin Top', placeholder: '10px', default: '' },
      'margin-right': { type: 'text', label: 'Margin Right', placeholder: '10px', default: '' },
      'margin-bottom': { type: 'text', label: 'Margin Bottom', placeholder: '10px', default: '' },
      'margin-left': { type: 'text', label: 'Margin Left', placeholder: '10px', default: '' },
      'padding': {
        type: 'text',
        label: 'Padding (all)',
        placeholder: '10px, 1rem, 10px 20px, etc.',
        default: ''
      },
      'padding-top': { type: 'text', label: 'Padding Top', placeholder: '10px', default: '' },
      'padding-right': { type: 'text', label: 'Padding Right', placeholder: '10px', default: '' },
      'padding-bottom': { type: 'text', label: 'Padding Bottom', placeholder: '10px', default: '' },
      'padding-left': { type: 'text', label: 'Padding Left', placeholder: '10px', default: '' }
    }
  },

  border: {
    label: 'Border',
    icon: 'square-2-stack',
    description: 'Border width, style, color, and radius',
    tags: ['border', 'outline', 'stroke', 'edge', 'frame', 'radius', 'rounded'],
    // No specific editor - will use GenericPropertyGroupEditor
    properties: {
      'border': {
        type: 'text',
        label: 'Border (all)',
        placeholder: '1px solid #000, 2px dashed red',
        default: ''
      },
      'border-width': {
        type: 'text',
        label: 'Width',
        placeholder: '1px, 2px, thin, medium, thick',
        default: ''
      },
      'border-style': {
        type: 'select',
        label: 'Style',
        options: [
          { value: '', name: 'Default' },
          { value: 'none', name: 'None' },
          { value: 'solid', name: 'Solid' },
          { value: 'dashed', name: 'Dashed' },
          { value: 'dotted', name: 'Dotted' },
          { value: 'double', name: 'Double' },
          { value: 'groove', name: 'Groove' },
          { value: 'ridge', name: 'Ridge' },
          { value: 'inset', name: 'Inset' },
          { value: 'outset', name: 'Outset' }
        ],
        default: ''
      },
      'border-color': {
        type: 'color',
        label: 'Color',
        placeholder: '#000000, red, rgb(0,0,0)',
        default: ''
      },
      'border-radius': {
        type: 'text',
        label: 'Radius',
        placeholder: '5px, 10px 20px, 50%',
        default: ''
      }
    }
  },

  typography: {
    label: 'Typography',
    icon: 'document-text',
    description: 'Font and text styling',
    tags: ['typography', 'text', 'font', 'type', 'letter', 'word', 'line'],
    // No specific editor - will use GenericPropertyGroupEditor
    properties: {
      'color': {
        type: 'color',
        label: 'Text Color',
        placeholder: '#000000, red, rgb(0,0,0)',
        default: ''
      },
      'font-size': {
        type: 'text',
        label: 'Font Size',
        placeholder: '14px, 1rem, large, x-large',
        default: ''
      },
      'font-weight': {
        type: 'select',
        label: 'Font Weight',
        options: [
          { value: '', name: 'Default' },
          { value: 'normal', name: 'Normal' },
          { value: 'bold', name: 'Bold' },
          { value: '100', name: '100 (Thin)' },
          { value: '200', name: '200 (Extra Light)' },
          { value: '300', name: '300 (Light)' },
          { value: '400', name: '400 (Normal)' },
          { value: '500', name: '500 (Medium)' },
          { value: '600', name: '600 (Semi Bold)' },
          { value: '700', name: '700 (Bold)' },
          { value: '800', name: '800 (Extra Bold)' },
          { value: '900', name: '900 (Black)' }
        ],
        default: ''
      },
      'font-family': {
        type: 'text',
        label: 'Font Family',
        placeholder: 'Arial, "Times New Roman", serif',
        default: ''
      },
      'line-height': {
        type: 'text',
        label: 'Line Height',
        placeholder: '1.5, 24px, normal',
        default: ''
      },
      'text-align': {
        type: 'select',
        label: 'Text Align',
        options: [
          { value: '', name: 'Default' },
          { value: 'left', name: 'Left' },
          { value: 'center', name: 'Center' },
          { value: 'right', name: 'Right' },
          { value: 'justify', name: 'Justify' }
        ],
        default: ''
      }
    }
  },

  layout: {
    label: 'Layout',
    icon: 'view-columns',
    description: 'Display, position, and layout properties',
    tags: ['layout', 'display', 'position', 'flex', 'grid', 'flow', 'arrange'],
    // No specific editor - will use GenericPropertyGroupEditor
    properties: {
      'display': {
        type: 'select',
        label: 'Display',
        options: [
          { value: '', name: 'Default' },
          { value: 'block', name: 'Block' },
          { value: 'inline', name: 'Inline' },
          { value: 'inline-block', name: 'Inline Block' },
          { value: 'flex', name: 'Flex' },
          { value: 'grid', name: 'Grid' },
          { value: 'none', name: 'None' }
        ],
        default: ''
      },
      'position': {
        type: 'select',
        label: 'Position',
        options: [
          { value: '', name: 'Default' },
          { value: 'static', name: 'Static' },
          { value: 'relative', name: 'Relative' },
          { value: 'absolute', name: 'Absolute' },
          { value: 'fixed', name: 'Fixed' },
          { value: 'sticky', name: 'Sticky' }
        ],
        default: ''
      },
      'top': { type: 'text', label: 'Top', placeholder: '10px, 50%, auto', default: '' },
      'right': { type: 'text', label: 'Right', placeholder: '10px, 50%, auto', default: '' },
      'bottom': { type: 'text', label: 'Bottom', placeholder: '10px, 50%, auto', default: '' },
      'left': { type: 'text', label: 'Left', placeholder: '10px, 50%, auto', default: '' },
      'z-index': { type: 'text', label: 'Z-Index', placeholder: '1, 999, auto', default: '' }
    }
  },

  size: {
    label: 'Size',
    icon: 'arrows-pointing-out',
    description: 'Width, height, and size constraints',
    tags: ['size', 'width', 'height', 'dimension', 'scale', 'min', 'max'],
    // No specific editor - will use GenericPropertyGroupEditor
    properties: {
      'width': { type: 'text', label: 'Width', placeholder: '100px, 50%, auto', default: '' },
      'height': { type: 'text', label: 'Height', placeholder: '100px, 50%, auto', default: '' },
      'min-width': { type: 'text', label: 'Min Width', placeholder: '100px, 50%', default: '' },
      'min-height': { type: 'text', label: 'Min Height', placeholder: '100px, 50%', default: '' },
      'max-width': { type: 'text', label: 'Max Width', placeholder: '100px, 50%', default: '' },
      'max-height': { type: 'text', label: 'Max Height', placeholder: '100px, 50%', default: '' }
    }
  },

  effects: {
    label: 'Effects',
    icon: 'sparkles',
    description: 'Visual effects and transitions',
    tags: ['effects', 'transition', 'transform', 'animation', 'shadow', 'opacity', 'visual'],
    // No specific editor - will use GenericPropertyGroupEditor
    properties: {
      'opacity': {
        type: 'text',
        label: 'Opacity',
        placeholder: '0.5, 0.8, 1 (0-1)',
        default: ''
      },
      'transform': {
        type: 'text',
        label: 'Transform',
        placeholder: 'rotate(45deg), scale(1.2), translateX(10px)',
        default: ''
      },
      'transition': {
        type: 'text',
        label: 'Transition',
        placeholder: 'all 0.3s ease, opacity 0.5s',
        default: ''
      },
      'box-shadow': {
        type: 'text',
        label: 'Box Shadow',
        placeholder: '0 2px 4px rgba(0,0,0,0.1)',
        default: ''
      }
    }
  },

  modifiers: {
    type: 'modifiers',
    label: 'CSS Modifiers',
    icon: 'cursor-arrow-rays',
    description: 'Pseudo-classes like :hover, :focus, :nth-child',
    tags: ['mod', 'modifier', 'modifiers', 'pseudo', 'pseudoclass', 'class', 'state', 'interaction', 'css', 'hover', 'focus', 'active'],
    definitions: modifierDefinitions
  }
}

// Property groups order for UI display
export const propertyGroupOrder = [
  'modifiers', // Put modifiers first as it's fundamental to CSS classes
  'background',
  'typography',
  'spacing',
  'border',
  'size',
  'layout',
  'effects'
]

// Get all individual properties from property group definitions
export const getAllProperties = () => {
  const allProperties = {}

  Object.values(propertyDefinitions).forEach(group => {
    if (group.properties) {
      Object.assign(allProperties, group.properties)
    }
  })

  return allProperties
}

// Get property group that contains a specific CSS property
export const getPropertyGroup = (cssProperty) => {
  for (const [groupKey, group] of Object.entries(propertyDefinitions)) {
    if (group.properties && group.properties[cssProperty]) {
      return groupKey
    }
  }
  return null
}

// Get default properties object with all available properties
export const getDefaultProperties = () => {
  const defaults = {}

  Object.values(propertyDefinitions).forEach(group => {
    if (group.properties) {
      Object.entries(group.properties).forEach(([property, config]) => {
        defaults[property] = config.default || ''
      })
    }
  })

  return defaults
}